import CheckoutSidebar, {
  CheckoutSidebarProps,
} from "@/features/CheckoutSidebar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Divider } from "@/components/ui/divider";
import { Input } from "@/components/ui/input";
import { faChevronDown, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@radix-ui/react-accordion";
import { useState, memo } from "react";
import {
  DriverProps,
  PreAuthorizationProps,
  PriceProps,
  PriceResultAllExtra,
  PriceResultProps,
  StationResultProps,
} from "@/lib/type";
import { emailRegex, phoneRegex, countryPhoneCodes, MINIMUM_DRIVER_AGE } from "@/lib/constant";
import { TextArea } from "@/components/ui/textarea";
import Datepicker from "react-tailwindcss-datepicker";
import { DateInput } from "@/components/ui/dateInput";
import { toast } from "sonner";
import { Tooltip } from "@/components/ui/tooltip";
import { useRegion } from "@/context/RegionContext";
import { formatCurrencySymbol, formatCurrencyAmount } from "@/lib/utils";
import { validateDiscountCode } from "@/lib/service";

// Discount code validation response interface
interface DiscountCodeResponse {
  status: string;
  data: {
    valid: boolean;
    code: string;
    type: string;
    value: string;
    discount_amount: number;
    final_amount: number;
    remaining_usage: number;
    message: string;
  };
  message: string;
}

const Checkout = ({
  car,
  total,
  pickUp,
  dropOff,
  extras,
  rentalDays,
  discount,
  delivery,
}: CheckoutSidebarProps) => {
  const { region } = useRegion();
  return (
    <>
      <CheckoutSidebar
        car={car}
        total={total}
        pickUp={pickUp}
        dropOff={dropOff}
        extras={extras}
        rentalDays={rentalDays}
        discount={discount}
        delivery={delivery}
      />
      <div className="my-2 flex flex-col space-y-2 text-sm font-bold text-green-600">
        <Accordion type="single" collapsible>
          <AccordionItem value="deposit">
            <AccordionTrigger onClick={() => {}}>
              <p className="font-bold text-green-600">
                Deposit <FontAwesomeIcon icon={faChevronDown} />
              </p>
            </AccordionTrigger>
            <AccordionContent className="text-black text-sm font-normal">
              The amount will be pre-authorised on your credit card at the time of rental pickup, but not debited from your account. This amount will be blocked on your credit card at pick-up time, or in case of Online Check-In, it will be blocked within 48 hours before the pick-up. Deposit will be used for extra charges (e.g. fuel missing at return, extra-day, extra-mileage, one-way charge, etc.). If you need further information about deposit release time and deposit's amount, please{" "}
              <a href="/pages/deposit-policy/" className="font-bold text-green-600">
                click here
              </a>
              .
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Accordion type="single" collapsible>
          <AccordionItem value="fuel-policy">
            <AccordionTrigger onClick={() => {}}>
              <p className="font-bold text-green-600">
                Fuel Policy <FontAwesomeIcon icon={faChevronDown} />
              </p>
            </AccordionTrigger>
            <AccordionContent className="text-black text-sm font-normal">
              Return the vehicle with the same amount of fuel or use our refueling service (price communicated at pickup).
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Accordion type="single" collapsible>
          <AccordionItem value="cancellation-policy">
            <AccordionTrigger onClick={() => {}}>
              <p className="font-bold text-green-600">
                Cancellation Policy{" "}
                <FontAwesomeIcon icon={faChevronDown} />
              </p>
            </AccordionTrigger>
            <AccordionContent className="text-black text-sm font-normal">
              Cancellations made over 48 hours before pick-up will incur a fee based on the Stripe Internet Payment Gateway processing charges (please refer to{" "}
              <a
                href={region.stripePricingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-green-600 hover:underline"
              >
                {region.stripePricingUrl}
              </a>
              ). No refunds for cancellations within 48 hours of pick-up.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  );
};

interface ConfirmReservationProps {
  onPay: () => Promise<void>;
  onChange: any;
  car: PriceResultProps;
  driverDetail?: DriverProps;
  cardDetail?: PreAuthorizationProps;
  review?: PriceProps;
  extras: PriceResultAllExtra[];
  rentalDays: number;
  dropOff?: StationResultProps;
  pickUp?: StationResultProps;
  onDiscountApplied?: (discountData: any) => void;
  vehicleApiUrl?: string;
  delivery?: CheckoutSidebarProps["delivery"];
}

const ConfirmReservation: React.FC<ConfirmReservationProps> = ({
  onPay,
  onChange,
  car,
  driverDetail,
  // cardDetail,
  review,
  extras,
  rentalDays,
  dropOff,
  pickUp,
  onDiscountApplied,
  vehicleApiUrl,
  delivery,
}) => {
  const { region } = useRegion();
  const currencySymbol = formatCurrencySymbol(car.pricing.currency);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isError, setIsError] = useState(false);
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(region.defaultPhoneCode);
  const [emailConfirmation, setEmailConfirmation] = useState("");
  
  // Discount code states
  const [discountCode, setDiscountCode] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState("");

  // Age validation utility function
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const isValidAge = (birthDate: string): boolean => {
    if (!birthDate) return false;
    const age = calculateAge(birthDate);
    return age >= MINIMUM_DRIVER_AGE;
  };

  const handlePayNowClick = async () => {
    try {
      await onPay();
    } catch (error: any) {
      toast("Unknown error occurred during payment.");
    }
  };

  // Handle discount code application
  const handleApplyDiscount = async () => {
    const trimmedCode = discountCode.trim();
    
    if (!trimmedCode) {
      setDiscountError("Please enter a coupon code");
      return;
    }

    if (trimmedCode.length > 20) {
      setDiscountError("Coupon code cannot exceed 20 characters");
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError("");

    try {
      // Discount applies to the rental base only (delivery is never discounted),
      // matching the server which discounts Payment.paymentAmount (base).
      const deliveryTotal = (delivery ?? []).reduce((sum, leg) => sum + leg.fee, 0);
      const totalAmount = Math.max(0, (review?.price || 0) - deliveryTotal);
      const data: DiscountCodeResponse = await validateDiscountCode(trimmedCode, totalAmount, vehicleApiUrl);

      if (data.status === "success" && data.data.valid) {
        setAppliedDiscount(data.data);
        toast.success(`Coupon applied! You saved ${formatCurrencyAmount(data.data.discount_amount, car.pricing.currency)}`);

        // Update the parent component with discount data
        if (onDiscountApplied) {
          onDiscountApplied(data.data);
        }
      } else {
        setDiscountError(data.message || "Invalid coupon code");
        toast.error(data.message || "Invalid coupon code");
      }
    } catch (error) {
      setDiscountError("Failed to validate coupon code. Please try again.");
      toast.error("Failed to validate coupon code. Please try again.");
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Remove applied discount
  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
    
    if (onDiscountApplied) {
      onDiscountApplied(null);
    }
    
    toast.success("Coupon removed");
  };

  return (
    <>
      <div className="flex flex-row items-center justify-between font-bold mb-2">
        <p className="text-2xl">Confirm your booking</p>
        <div className="flex flex-col text-end text-sm">
          <p className="font-normal">TOTAL</p>
          {appliedDiscount ? (
            <div className="flex flex-col">
              <p className="text-sm line-through text-neutral-500">
                {formatCurrencyAmount(review?.price ?? 0, car.pricing.currency)}
              </p>
              <p className="text-green-600">
                {formatCurrencyAmount(appliedDiscount.final_amount + (delivery ?? []).reduce((sum, leg) => sum + leg.fee, 0), car.pricing.currency)}
              </p>
            </div>
          ) : (
            <p>
              {formatCurrencyAmount(review?.price ?? 0, car.pricing.currency)}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr] gap-8">
        {/* Main content */}
        <div className="space-y-6">
          {/* Discount Code Section */}
          <div className="bg-neutral-100 p-6 rounded-lg space-y-4">
            <p className="font-bold text-lg">Add a coupon code</p>
            {!appliedDiscount ? (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Input
                    id="discountCode"
                    placeholder="Enter coupon code - 20 characters max."
                    className="bg-white flex-1"
                    value={discountCode}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement).value;
                      if (value.length <= 20) {
                        setDiscountCode(value);
                      }
                      if (discountError) setDiscountError("");
                    }}
                    showError={!!discountError}
                    hideErrorMessage={true}
                  />
                  <Button
                    onClick={handleApplyDiscount}
                    disabled={isApplyingDiscount || !discountCode.trim()}
                    className="px-6"
                  >
                    {isApplyingDiscount ? "Applying..." : "Apply"}
                  </Button>
                </div>
                {discountError && (
                  <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1">
                    <FontAwesomeIcon icon={faInfoCircle} />
                    <span>{discountError}</span>
                  </div>
                )}
                <p className="text-xs text-neutral-500">
                  Maximum 20 characters. Enter your coupon code to get savings on your booking.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-green-800">
                      Coupon Applied: {appliedDiscount.code}
                    </p>
                    <p className="text-sm text-green-600">
                      {appliedDiscount.type === "PERCENTAGE"
                        ? `${appliedDiscount.value}% off`
                        : `${currencySymbol} ${appliedDiscount.value} off`}
                      {" - "}You saved {formatCurrencyAmount(appliedDiscount.discount_amount, car.pricing.currency)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveDiscount}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-neutral-100 p-6 rounded-lg space-y-4">
            <p className="mb-6">
              <span className="font-bold text-lg">Driver details</span>{" "}
              <span className="text-xs">* Mandatory information</span>
            </p>
            <Input
              title="First name*"
              id="firstName"
              placeholder=""
              className="bg-white"
              onChange={(event) => {
                setFirstName((event.target as HTMLInputElement).value);
                onChange({ name: `${(event.target as HTMLInputElement).value} ${lastName}` });
              }}
              showError={isError && firstName == ""}
            />
            <Input
              title="Last name*"
              id="lastName"
              placeholder=""
              className="bg-white"
              onChange={(event) => {
                setLastName((event.target as HTMLInputElement).value);
                onChange({ name: `${firstName} ${(event.target as HTMLInputElement).value}` });
              }}
              showError={isError && lastName == ""}
            />
            <Input
              title="Date of birth*"
              id="birthDate"
              placeholder="DD / MM / YYYY"
              className="bg-white appearance-none"
              type="date"
              onChange={(event) => {
                const dateValue = (event.target as HTMLInputElement).value;
                console.log("Date selected:", dateValue);
                // Format date as YYYY-MM-DD for backend processing
                onChange({
                  birthDate: dateValue,
                });
              }}
              value={driverDetail?.dateOfBirth || ""}
              showError={
                isError && 
                (driverDetail?.dateOfBirth === undefined || 
                 (driverDetail?.dateOfBirth !== undefined && !isValidAge(driverDetail.dateOfBirth)))
              }
              hideErrorMessage
            />
            {/* Custom error messages for date of birth */}
            {isError && driverDetail?.dateOfBirth == undefined && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Date of birth cannot be empty</span>
              </div>
            )}
            {isError && driverDetail?.dateOfBirth != undefined && !isValidAge(driverDetail.dateOfBirth) && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Driver must be at least {MINIMUM_DRIVER_AGE} years old to rent a vehicle</span>
              </div>
            )}
            <div className="flex flex-row items-center space-x-2">
              <p className="font-normal">Email*</p>
              <Tooltip
                id="email-info"
                title=""
                content={
                  <span>
                    Europcar may send you a reminder email about this specific
                    reservation to help you complete your booking if you wish.
                    No marketing emails will be sent unless you give your express
                    permission.
                  </span>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="text-neutral-300" />
              </Tooltip>
            </div>
            <Input
              id="email"
              placeholder="Email"
              className="bg-white"
              type="email"
              onChange={(event) =>
                onChange({ email: (event.target as HTMLInputElement).value })
              }
              showError={
                isError &&
                (driverDetail?.email == undefined ||
                  !emailRegex.test(driverDetail.email))
              }
              hideErrorMessage={true}
            />
            {/* Custom error message for email */}
            {isError && driverDetail?.email == undefined && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Email cannot be empty</span>
              </div>
            )}
            {isError && driverDetail?.email != undefined && !emailRegex.test(driverDetail.email) && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Email format is wrong</span>
              </div>
            )}
            {/* Email Confirmation Field */}
            <div className="flex flex-row items-center space-x-2">
              <p className="font-normal">Confirm Email*</p>
              <Tooltip
                id="email-confirm-info"
                title=""
                content={
                  <span>
                    Please re-enter your email address.
                  </span>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="text-neutral-300" />
              </Tooltip>
            </div>
            <Input
              id="emailConfirmation"
              placeholder="Confirm Email"
              className="bg-white"
              type="email"
              value={emailConfirmation}
              onChange={(event) =>
                setEmailConfirmation((event.target as HTMLInputElement).value)
              }
              onPaste={(event) => {
                event.preventDefault();
                return false;
              }}
              onCopy={(event) => {
                event.preventDefault();
                return false;
              }}
              showError={
                isError &&
                (emailConfirmation === "" ||
                  (driverDetail?.email !== undefined && 
                   emailConfirmation !== "" && 
                   driverDetail.email !== emailConfirmation))
              }
              hideErrorMessage={true}
            />
            {/* Custom error message for email confirmation */}
            {isError && emailConfirmation === "" && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Confirm Email cannot be empty</span>
              </div>
            )}
            {isError && emailConfirmation !== "" && driverDetail?.email !== undefined && driverDetail.email !== emailConfirmation && (
              <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Email addresses do not match</span>
              </div>
            )}
            <div className="relative">
              <p className="font-bold mb-2">Phone number*</p>
              <div className="flex gap-2">
                <select
                  className="bg-white border border-gray-300 rounded-md px-2 h-10 w-[140px] appearance-none"
                  value={selectedCountryCode}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                >
                  {countryPhoneCodes.map((country) => (
                    <option key={country.iso} value={country.code}>
                      {country.iso} {country.code}
                    </option>
                  ))}
                </select>
                <Input
                  id="phoneNo"
                  placeholder="Phone number"
                  className="bg-white flex-1 h-10"
                  type="tel"
                  onChange={(event) => {
                    const phoneNumber = `${selectedCountryCode}${(event.target as HTMLInputElement).value}`;
                    onChange({ phoneNo: phoneNumber });
                  }}
                  showError={
                    isError &&
                    (driverDetail?.phone == undefined ||
                      !RegExp(/^\+\d{1,3}\d{6,14}$/).test(driverDetail.phone))
                  }
                  hideErrorMessage={true}
                />
              </div>
              {isError && driverDetail?.phone == undefined && (
                <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>Phone number cannot be empty</span>
                </div>
              )}
              {isError && driverDetail?.phone != undefined && !RegExp(/^\+\d{1,3}\d{6,14}$/).test(driverDetail.phone) && (
                <div className="text-xs text-red-500 font-medium flex flex-row items-center space-x-1 mt-1">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>Phone number format is wrong</span>
                </div>
              )}
            </div>
            <Divider className="py-3" />
            <div className="font-bold text-lg flex items-center gap-2">
              Flight Information{" "}
              <Tooltip
                id="flight-info"
                title=""
                content={
                  <span className="text-left font-normal">
                    By entering your flight number here (e. g. AF903), in the
                    event you are delayed, we will wait for up to 1 hour - even
                    if we should be closed!
                  </span>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="text-neutral-300" />
              </Tooltip>
            </div>
            <Input
              id="flightNo"
              title="Flight number"
              placeholder=""
              className="bg-white w-7/12"
              onChange={(event) =>
                onChange({ flightNo: (event.target as HTMLInputElement).value })
              }
            />
          </div>

          {/* Payment Method */}
          <div className="space-y-4">
            <p className="font-bold text-lg">Payment Method</p>
            <div className="flex items-center gap-4 p-4 border-2 rounded-lg w-fit">
              <div className="flex flex-col">
                <p className="font-medium">Credit Card</p>
                <p className="text-sm">Pay with Stripe</p>
              </div>
              <img src="../icon/stripe_logo.svg" className="w-24" />
            </div>
            <Checkbox
              id="acceptPrivacy"
              label="I have read, understood and I accept the"
              tooltipChild={
                <a
                  href={`${region.productionUrl}/pages/pdpa`}
                  target="_blank"
                >
                  <span className="font-bold text-green-600">
                    Privacy Policy of Europcar
                  </span>
                </a>
              }
              onClick={() => setIsPrivacyChecked((prev) => !prev)}
              checkboxClassName="w-6 h-6"
              className="py-2"
              value={isPrivacyChecked}
              showError={isError && !isPrivacyChecked}
              errorMessage="Privacy Policy acceptance cannot be empty"
            />
            <Checkbox
              id="acceptTerms"
              label="I acknowledge that I have read, understood and agree the"
              tooltipChild={
                <a
                  href={`${region.productionUrl}/pages/terms-conditions`}
                  target="_blank"
                >
                  <span className="font-bold text-green-600">
                    Europcar Terms and Conditions
                  </span>
                </a>
              }
              onClick={() => setIsTermsChecked((prev) => !prev)}
              checkboxClassName="w-6 h-6"
              className="pb-2"
              value={isTermsChecked}
              showError={isError && !isTermsChecked}
              errorMessage="Terms and Conditions acceptance cannot be empty"
            />
            <Button
              className="md:w-1/6 h-12"
              onClick={() => {
                if (
                  firstName === "" ||
                  lastName === "" ||
                  driverDetail?.dateOfBirth == undefined ||
                  !isValidAge(driverDetail?.dateOfBirth || "") ||
                  driverDetail?.email == undefined ||
                  !RegExp(
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                  ).test(driverDetail.email) ||
                  emailConfirmation === "" ||
                  driverDetail.email !== emailConfirmation ||
                  driverDetail?.phone == undefined ||
                  !isPrivacyChecked ||
                  !isTermsChecked
                ) {
                  setIsError(true);
                } else {
                  setIsError(false);
                  handlePayNowClick();
                }
              }}
            >
              Pay Now
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {review == undefined ? (
            <></>
          ) : (
            <Checkout
              car={car}
              total={review}
              pickUp={pickUp}
              dropOff={dropOff}
              extras={extras}
              rentalDays={rentalDays}
              discount={appliedDiscount}
              delivery={delivery}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default memo(ConfirmReservation);
